/**
 * The little identity row above a message run — avatar + name + time —
 * adapted from aurora's chat MessageHeader for the two-party couple↔Éros
 * thread. Rendered once at the start of each consecutive same-sender run so
 * the thread reads as a conversation, not a wall of bubbles.
 *
 * No presence dot (unlike aurora's RecipientAvatar): Éros is a companion, not
 * a person who is "online" — a status dot would read wrong here.
 *
 * Time is localized: EN 12-hour (6:36 PM), EL 24-hour (18:36) — the natural
 * Greek clock. `i18n.language` can be hyphen-less ("elGR"); normalize to a
 * dayjs locale before formatting (mirrors AssistantHistoryList).
 */

import { Avatar, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/el';
import { useTranslation } from 'react-i18next';
import IconifyIcon from 'components/base/IconifyIcon';
import { getInitials } from 'lib/utils';

interface AssistantMessageHeaderProps {
  role: 'user' | 'assistant';
  /** ISO timestamp; omitted while a reply is still streaming (no time yet). */
  createdAt?: string;
  /** Current couple's display name + avatar for their own turns. */
  userName?: string;
  userAvatar?: string | null;
}

const AVATAR_SIZE = 24;

const AssistantMessageHeader = ({
  role,
  createdAt,
  userName,
  userAvatar,
}: AssistantMessageHeaderProps) => {
  const { t, i18n } = useTranslation();
  const isUser = role === 'user';
  const dateLocale = (i18n.language || 'en').startsWith('el') ? 'el' : 'en';

  const displayName = isUser ? t('assistant.message.you') : t('assistant.message.name');
  const time = createdAt
    ? dayjs(createdAt)
        .locale(dateLocale)
        .format(dateLocale === 'el' ? 'HH:mm' : 'h:mm A')
    : '';

  const avatar = isUser ? (
    <Avatar
      src={userAvatar ?? undefined}
      alt={userName}
      sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE, fontSize: 11 }}
    >
      {userName ? getInitials(userName) : null}
    </Avatar>
  ) : (
    <Avatar
      sx={{
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
      }}
    >
      <IconifyIcon icon="material-symbols:auto-awesome" sx={{ fontSize: 14 }} />
    </Avatar>
  );

  return (
    <Stack
      direction={isUser ? 'row-reverse' : 'row'}
      spacing={1}
      alignItems="center"
      // Extra top space separates a new sender's group from the bubble above;
      // it only renders at a run start, so consecutive bubbles stay tight.
      sx={{ mt: 1, mb: 0.25 }}
    >
      {avatar}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
        {displayName}
      </Typography>
      {time && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {time}
        </Typography>
      )}
    </Stack>
  );
};

export default AssistantMessageHeader;
